import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./users.css";
import {
  Table,
  Avatar,
  Input,
  Badge,
  message,
  ConfigProvider,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";
import Loader from "../../components/loader/loader";
import NotificationBell from "../../components/notificationBell/notificationBell";
import dayjs from "dayjs";

const Users = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    getCustomers();
  }, []);

  const getCustomers = () => {
    let getRes = (res) => {
      if (res.status === 200) {
        // Handle response structure: res.data.data.data
        const customersData = res.data?.data?.data || res.data?.data || res.data || [];
        setAllCustomers(customersData);
        
        // Apply local search filter if searchText exists
        if (searchText) {
          const lower = searchText.toLowerCase();
          const filtered = customersData.filter((customer) =>
            (customer.name || "").toLowerCase().includes(lower)
          );
          setCustomers(filtered);
          setPagination((prev) => ({
            ...prev,
            total: filtered.length || 0,
          }));
        } else {
          setCustomers(customersData);
          // Total count might be in res.size or res.data.data.length
          if (res.size !== undefined) {
            setPagination((prev) => ({
              ...prev,
              total: res.size || 0,
            }));
          } else if (res.data?.data?.length !== undefined) {
            setPagination((prev) => ({
              ...prev,
              total: res.data.data.length || 0,
            }));
          }
        }
      }
    };

    callApi(
      "GET",
      routes.getAllCustomers,
      null,
      setIsLoading,
      getRes,
      (error) => {
        message.error("Failed to load customers");
      }
    );
  };

  const handleSearch = (value) => {
    const text = value || "";
    setSearchText(text);
    setPagination((prev) => ({ ...prev, current: 1 }));

    if (!text) {
      setCustomers(allCustomers);
      setPagination((prev) => ({
        ...prev,
        total: allCustomers.length || 0,
      }));
      return;
    }

    const lower = text.toLowerCase();
    const filtered = allCustomers.filter((customer) =>
      (customer.name || "").toLowerCase().includes(lower)
    );
    setCustomers(filtered);
    setPagination((prev) => ({
      ...prev,
      total: filtered.length || 0,
    }));
  };

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const getLocation = (addresses) => {
    if (!addresses || addresses.length === 0) return "N/A";
    const defaultAddress = addresses.find((addr) => addr.isDefault) || addresses[0];
    return defaultAddress?.location?.address || "N/A";
  };


  const columns = [
    {
      title: "No",
      key: "index",
      width: 60,
      render: (_, __, index) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="customer-name-cell">
          <Avatar
            src={record.image}
            icon={<UserOutlined />}
            size={32}
            style={{ backgroundColor: "#FF66B2" }}
          />
          <span className="customer-name">{text || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "N/A",
    },
    {
      title: "Phone Number",
      dataIndex: "number",
      key: "number",
      render: (phone) => phone || "N/A",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Badge
          status={role === "admin" ? "success" : "default"}
          text={role || "user"}
        />
      ),
    },
    {
      title: "Subscription",
      dataIndex: "subscriptionPlan",
      key: "subscriptionPlan",
      render: (plan) => plan || "free",
    },
    {
      title: "Joined Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        date ? dayjs(date).format("DD MMM YYYY") : "N/A",
    },
    {
      title: "Verified",
      dataIndex: "verified",
      key: "verified",
      render: (verified) => (
        <Badge
          status={verified ? "success" : "error"}
          text={verified ? "Verified" : "Not Verified"}
        />
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="customers-container">
        <Loader loading={isLoading} />
        <div className="customers-header">
          <h1 className="customers-title">Customers</h1>
          <NotificationBell />
        </div>

        <div className="customers-card">
          <div className="customers-card-header">
            <h2 className="customers-card-title">Customers Details</h2>
            <Input
              placeholder="Search customers by name..."
              prefix={<SearchOutlined />}
              allowClear
              onChange={(e) => handleSearch(e.target.value)}
              style={{ maxWidth: 300 }}
            />
          </div>

          <Table
            columns={columns}
            dataSource={customers}
            loading={isLoading}
            rowKey="_id"
            scroll={{ x: "max-content", y: "calc(100vh - 400px)" }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} customers`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableChange}
            className="customers-table"
          />
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Users;
